import {Mental} from "../../Mental";
import {TimelineLite, TweenLite} from "gsap";
import {ReelEnhancer, ReelEnhancerData} from "../../server/ReelEnhancerData";
import {IMentalGameData} from "../../server/MentalData";
import {MentalReel} from "../../reel/MentalReel";
import {ReelModelState, ReelModelSymbolData} from "../../../framework/reel/ReelModel";
import {StackedWild} from "../../reel/symbols/StackedWild";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {MentalHelper} from "../../utils/MentalHelper";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {FeaturePresentationHelper} from "../../utils/FeaturePresentationHelper";
import {MentalScatter2Explosion} from "../../features/MentalScatter2Explosion";
import {SoundManager} from "../../sound/SoundManager";
import {MentalSoundConfig} from "../../MentalSoundConfig";

export class XNudgePresentation {
    private _gameData: IMentalGameData;
    private _reelSymbols: StackedWild[] = [];
    private _reelSymbolId: number[] = [];
    private _multiplier: PIXI.BitmapText[] = [];
    private _NudgeFire: TimelineSprite[] = [];

    constructor() {
        this.createMultiplierAndFire();
    }

    public xNudgeInEnhancer(): boolean {
        return Mental.model.gameData.specialReelsSymbols.includes(ReelEnhancer.XNUDGE_WILDS);
    }

    /**
     * function to replace normal symbol with xNudge symbol
     */
    public replaceEnhancerSymbols(): TimelineLite {
        const tl: TimelineLite = new TimelineLite()
        this._reelSymbols = [];
        this._reelSymbolId = [];
        this._gameData = Mental.model.gameData;
        const enhancerData: ReelEnhancerData = Mental.model.reelEnhancerData;
        tl.to({}, {duration: 0.1});
        tl.add(() => {
            for (let i = 0; i < 2; i++) {
                if (this._gameData.specialReelsSymbols[i] === ReelEnhancer.XNUDGE_WILDS) {
                    const reel: MentalReel = Mental.reelArea.getReel(enhancerData.xWaysPossibleReels[i]);
                    // sym is the enhancer reel symbol
                    const enhancerSymbol: ReelModelSymbolData = reel.model.getStopSymbolAt(2);
                    const replacedSymbol: StackedWild = reel.model.addStopSymbolAt("W3", 2).positionable as StackedWild;
                    replacedSymbol._symbolLayer.position.y = 396;
                    this._reelSymbols.push(replacedSymbol);
                    this._reelSymbolId.push(i)
                    replacedSymbol.clear()
                    reel.removeSymbol(enhancerSymbol, ReelModelState.STOPPED);
                }
            }
        });
        return tl;
    }

    public hideMultiOnSpinStart(): void {
        if (!this._reelSymbolId.length) {
            return;
        }
        for (let i: number = 0; i < this._reelSymbolId.length; i++) {
            const id: number = this._reelSymbolId[i];
            TweenLite.to({}, {duration: 0.3}).eventCallback("onUpdate", () => {
                this._multiplier[id].position.y = MentalHelper.copyLocalPosition(this._multiplier[id], this._reelSymbols[i]._symbolLayer.children[0]).y;
                if (this._multiplier[id].scale.x === 0.6) {
                    this._multiplier[id].position.y += 99 + (this._multiplier[id].height * 1.5);
                } else {
                    this._multiplier[id].position.y += 99 + (this._multiplier[id].height);
                }
            }).eventCallback("onComplete", () => {
                this._multiplier[id].visible = false;
                this._multiplier[id].text = "x1";
            });
        }
    }

    /**
     * function to nudge reels
     */
    public playXNudgePresentation(index: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const actualReelId: number = (index === 0 ? 1 : 3);
        const enhancerData: ReelEnhancerData = Mental.model.reelEnhancerData;
        const i: number = this._reelSymbolId.indexOf(index);
        const nudgeReelId: number = this._reelSymbolId[i];
        const stepSizes: number[] = [];

        Mental.xWaysCounter.updateXwaysCounterFromXNudge(actualReelId);

        tl.add(() => this.resetSpines());

        // #144
        if (actualReelId === 3) {
            // #195
            tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION[(MentalHelper.getRandomIntInRange(0, 2))]), 0);

            tl.add(MentalScatter2Explosion.playScatter2ExplosionAnimation(actualReelId, 2, 0, false, 634.5, 99));
        }

        if (FeaturePresentationHelper._symbolHeightOnReel[actualReelId] > -1) {
            tl.add(() => {
                // only when resize already happened
                FeaturePresentationHelper.resizeSymbolWithInReel(actualReelId, false);
            });
        } else {
            const distance: number[] = this._gameData.currentSymbolSize[actualReelId];
            for (let j = 0; j < distance.length - 1; j++) { // -1 for not including enhancer
                for (let k = 0; k < distance[j]; k++) {
                    stepSizes.unshift(198 / distance[j]);
                }
            }
        }

        if (this._gameData.specialReelsSymbols[nudgeReelId] === ReelEnhancer.XNUDGE_WILDS) {
            tl.add(() => {
                (this._reelSymbols[i]._symbolLayer.children[0] as PIXI.spine.Spine).state.setAnimation(0, "stop", false)
            });

            const reelId: number = nudgeReelId;
            const steps: number = this._gameData.multiplierOnReel[Mental.model.reelEnhancerData.xWaysPossibleReels[reelId]] - 1;

            let totalDistance: number = 0;
            for (let j = 0; j < steps; j++) {
                if (!stepSizes.length) {
                    totalDistance += 396 / steps;
                } else {
                    totalDistance += stepSizes[j];
                }
                tl.add(Mental.reelArea.nudgeSymbol(this._reelSymbols[i]._symbolLayer.children[0], enhancerData.xWaysPossibleReels[reelId], -1, totalDistance)
                    .eventCallback("onUpdate", () => {
                        this._multiplier[reelId].visible = true;
                        this._multiplier[reelId].position.y = MentalHelper.copyLocalPosition(this._multiplier[reelId], this._reelSymbols[i]._symbolLayer.children[0]).y;
                        if (this._multiplier[reelId].scale.x === 0.6) {
                            this._multiplier[reelId].position.y += 99 + (this._multiplier[reelId].height * 1.5);
                        } else {
                            this._multiplier[reelId].position.y += 99 + (this._multiplier[reelId].height);
                        }
                    })
                    .eventCallback("onStart", () => {
                        // #203
                        SoundManager.playSoundEffect(MentalSoundConfig.NUDGE);
                        const multiplier: PIXI.BitmapText = this._multiplier[reelId];
                        // update Multiplier from here
                        let multi: number = Number((multiplier.text).slice(1, multiplier.text.length));
                        multiplier.text = "x" + (++multi);

                        if (multiplier.text.length > 2) {
                            multiplier.scale.set(0.6);
                            const index: number = reelId === 0 ? 1 : 3;
                            multiplier.position.x = (140 * index) + multiplier.width / 2;
                        } else {
                            multiplier.scale.set(1);
                        }
                        multiplier.pivot.set(multiplier.width / 2, multiplier.height / 2);

                        this._NudgeFire[reelId].stopLoop();
                        this._NudgeFire[reelId].hide();
                        this._NudgeFire[reelId].position.y = MentalHelper.copyLocalPosition(this._NudgeFire[reelId], this._reelSymbols[i]._symbolLayer.children[0]).y;
                        this._NudgeFire[reelId].position.y -= 99 + this._NudgeFire[reelId].height;
                        this._NudgeFire[reelId].getAnimationAutoShowHide(true, true);
                    }));
                tl.to(this, {duration: 0.3});
            }
        }

        return tl;
    }

    public playNudgeWinPresentation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._gameData.betWayWins.length) {
            // #203, only play once
            tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.WILD_SKULL), 0);

            for (let i = 0; i < this._reelSymbols.length; i++) {
                tl.add(() => {
                    this._reelSymbols[i]._symbolLayer.mask = null;
                });
                tl.add(new SpineTween(this._reelSymbols[i]._symbolLayer.children[0] as PIXI.spine.Spine, 0, "win"), 0.01);
            }
        }
        tl.add(() => {
            this.resetSpines();
        });
        return tl;
    }

    public toggleTintForSpecialMultipliers(tint: number): void {
        for (let i: number = 0; i < this._reelSymbols.length; i++) {
            this._reelSymbols[i].doTint(tint);
            this._multiplier[i].tint = tint;
        }
    }

    private createMultiplierAndFire(): void {
        const layer: StageLayer = NolimitApplication.getLayerByName(Mental.LAYERS.X_NUDGE) as StageLayer;
        for (let i: number = 0; i < 2; i++) {
            const reelId: number = (i == 0 ? 1 : 3);
            const multi: PIXI.BitmapText = new PIXI.BitmapText("x99", {
                fontName: "xNudgeWinCountUp",
                fontSize: 50,
            });
            multi.pivot.set(multi.width / 2, multi.height / 2);
            multi.position.x = (144 * reelId) + multi.width / 2.75;
            multi.mask = Mental.reelArea.getReel(reelId).layerSet.layers[0].mask;
            this._multiplier.push(multi);
            multi.text = "x1";
            multi.visible = false;

            const fire: TimelineSprite = new TimelineSprite(Mental.assetLoader.getTextures("NudgeFireFrame"), 45);
            fire.blendMode = PIXI.BLEND_MODES.ADD;
            fire.position.x = 144 * reelId + fire.width / 1.8;
            fire.scale.set(2.5);
            fire.anchor.set(0.5);
            this._NudgeFire.push(fire);
            fire.visible = false;
            layer?.addChild(multi, fire);
        }
    }

    /**
     * function to reset spine
     * @private
     */
    private resetSpines(): void {
        for (let i: number = 0; i < this._reelSymbols.length; i++) {
            (this._reelSymbols[i]._symbolLayer.children[0] as PIXI.spine.Spine).skeleton.setToSetupPose();
            (this._reelSymbols[i]._symbolLayer.children[0] as PIXI.spine.Spine).state.clearTracks();
        }
    }
}